import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  browserSessionPersistence,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

// 비활성 타임아웃 시간 (12시간 = 43200000ms)
const IDLE_TIMEOUT = 12 * 60 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 권한 레벨 정의
export const ROLES = {
  ADMIN: 'admin',
  GUEST: 'guest',
  VISITOR: 'visitor',
  DEMO: 'demo' // 체험용 더미 데이터만 볼 수 있는 역할
};

// 권한 레벨 비교 함수
export const hasPermission = (userRole, requiredRole) => {
  const roleHierarchy = {
    [ROLES.VISITOR]: 0,
    [ROLES.DEMO]: 1,      // DEMO는 더미 데이터만 볼 수 있음
    [ROLES.GUEST]: 2,
    [ROLES.ADMIN]: 3
  };
  
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Firebase User 객체
  const [role, setRole] = useState(ROLES.VISITOR); // 사용자 권한
  const [loading, setLoading] = useState(true); // 인증 상태 확인 중
  const idleTimerRef = useRef(null); // 비활성 타이머 ref

  // 비활성 타이머 리셋 함수
  const resetIdleTimer = useCallback(() => {
    // 기존 타이머 취소
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    // 새 타이머 설정 (12시간 후 자동 로그아웃)
    idleTimerRef.current = setTimeout(async () => {
      if (auth.currentUser) {
        console.log('12시간 비활성으로 자동 로그아웃');
        try {
          await signOut(auth);
        } catch (error) {
          console.error('자동 로그아웃 오류:', error);
        }
      }
    }, IDLE_TIMEOUT);
  }, []);

  // Firebase 인증 상태 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Firestore에서 사용자 역할 가져오기
        await loadUserRole(firebaseUser);
        // 로그인 시 비활성 타이머 시작
        resetIdleTimer();
        
        // 로그인 시 삭제 예정 매물 정리
        try {
          const { cleanupScheduledDeletions } = await import('../utils/api');
          const result = await cleanupScheduledDeletions();
          if (result && result.deleted > 0) {
            console.log(`[로그인] 삭제 예정 매물 정리 완료: ${result.deleted}개`);
          }
        } catch (error) {
          console.warn('[로그인] 삭제 예정 매물 정리 실패:', error);
        }
      } else {
        setUser(null);
        setRole(ROLES.VISITOR);
        // 로그아웃 시 타이머 취소
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      // 컴포넌트 언마운트 시 타이머 정리
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [resetIdleTimer]);

  // 사용자 활동 감지 - 활동 시 타이머 리셋
  useEffect(() => {
    if (!user) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      resetIdleTimer();
    };

    // 이벤트 리스너 등록
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      // 이벤트 리스너 해제
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, resetIdleTimer]);

  // Firestore에서 사용자 역할 불러오기
  const loadUserRole = async (firebaseUser) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setRole(userData.role || ROLES.GUEST);
      } else {
        // 사용자 문서가 없으면 기본값으로 guest 설정
        setRole(ROLES.GUEST);
        // Firestore에 사용자 문서 생성
        await setDoc(doc(db, 'users', firebaseUser.uid), {
          email: firebaseUser.email || '',
          role: ROLES.GUEST,
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('사용자 역할 불러오기 오류:', error);
      setRole(ROLES.GUEST); // 오류 시 기본값
    }
  };

  // 이메일/비밀번호로 로그인
  const login = async (email, password, rememberMe = false) => {
    try {
      // 자동 로그인 체크 여부에 따라 persistence 설정
      if (rememberMe) {
        // 로컬 저장소 기반 로그인 - 브라우저를 닫아도 로그인 유지
        await setPersistence(auth, browserLocalPersistence);
      } else {
        // 세션 기반 로그인 - 브라우저 탭/창 닫으면 자동 로그아웃
        await setPersistence(auth, browserSessionPersistence);
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Firestore에서 사용자 역할 가져오기
      await loadUserRole(firebaseUser);
      
      return { success: true, user: firebaseUser };
    } catch (error) {
      console.error('로그인 오류:', error);
      let errorMessage = '로그인에 실패했습니다.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = '등록되지 않은 이메일입니다.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = '비밀번호가 올바르지 않습니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '유효하지 않은 이메일 형식입니다.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.';
      }
      
      return { success: false, message: errorMessage };
    }
  };

  // 로그아웃
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole(ROLES.VISITOR);
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const value = {
    user,
    role,
    login,
    logout,
    loading,
    isAuthenticated: user !== null,
    isAdmin: role === ROLES.ADMIN,
    isGuest: role === ROLES.GUEST,
    isVisitor: role === ROLES.VISITOR,
    isDemo: role === ROLES.DEMO
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

